import Image from 'next/image';
import React from 'react';
import { useRouter } from 'next/router';
import { Review, SCORES } from '@/utils/api/reviews/reviews.data-model';
import { Editor } from '@/shared/editor';
import { datetimeInLocaleLanguageAndTimezone } from '@/utils/timeFormatters';
import { cn } from '@/lib/tailwindcss-utils';
import { Genre } from '@/utils/api/genres/genres.data-model';
import Link from 'next/link';
import { twJoin } from 'tailwind-merge';

import RiCloseFill from '~icons/ri/close-fill';
import SimpleIconsSteam from '~icons/simple-icons/steam';
import SimpleIconsIgdb from '~icons/simple-icons/igdb';

export function ReviewDescription({
  review,
  genres,
  onClose,
  className,
}: {
  review: Review;
  genres: Genre[];
  onClose: () => void;
  className?: string;
}) {
  const router = useRouter();
  const scoreInfo = SCORES.find(s => s.value === review.score);

  const getGameLink = (aggregator: string, id: string, name: string) => {
    if (aggregator === 'steam')
      return `https://store.steampowered.com/app/${id}`;
    if (aggregator === 'igdb') {
      const slug = name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      return `https://www.igdb.com/games/${slug}`;
    }
    return '#';
  };

  return (
    <article
      className={twJoin(
        `relative order-first flex h-auto w-full flex-col gap-5 rounded-xl`,
        'md:sticky md:top-28 md:order-none md:max-h-[calc(100vh-15rem)] md:overflow-y-auto md:pr-5',
        className
      )}
    >
      <div className="relative">
        <div className="absolute right-0 order-first flex w-full flex-row items-center justify-end gap-3 text-center md:order-none">
          <button
            onClick={() => {
              onClose();
              router.push('/game-reviews', undefined, {
                shallow: true,
              });
            }}
            className="m-2 rounded-lg bg-[#0B0B0B] p-2 transition-colors duration-200 ease-in-out hover:bg-rose-700"
          >
            <RiCloseFill />
          </button>
        </div>

        <Image
          src={review.game.image}
          alt={review.game.name}
          width={640}
          height={320}
          className="max-h-[269px] w-full rounded-xl object-cover object-top"
        />
      </div>

      <section className="absolute left-3 top-3 flex flex-row flex-wrap items-center gap-2">
        <p className="rounded-xl bg-[#0B0B0B] px-4 py-2 text-center text-base">
          {scoreInfo?.label} {scoreInfo?.emoji}
        </p>
        <Link
          href={getGameLink(
            review.game.aggregatorType,
            review.game.gameId,
            review.game.name
          )}
          target="_blank"
          className="flex flex-row items-center gap-6 rounded-xl bg-[#0B0B0B] px-4 py-2"
        >
          {review.game.aggregatorType === 'steam' ? (
            <span className="flex flex-row items-center gap-2">
              <SimpleIconsSteam /> Steam
            </span>
          ) : (
            <span className="flex flex-row items-center gap-2">
              <SimpleIconsIgdb /> IGDB
            </span>
          )}
        </Link>
      </section>

      <section className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold">{review.game.name}</h1>
          <p className="text-zinc-500">Описание</p>
        </div>

        <div className="prose prose-neutral -mt-4 font-medium text-white dark:prose-invert">
          <Editor key={review.id} review={review} />
        </div>
      </section>

      <section className="flex flex-row flex-wrap justify-start gap-2">
        <div className="inline-flex w-full flex-row flex-wrap gap-2 md:flex-row">
          <p className="grow rounded-xl bg-[#0B0B0B] px-5 py-3 text-center">
            <span className="mr-2 text-zinc-500">Дата создания</span>
            {datetimeInLocaleLanguageAndTimezone(new Date(review.createdAt))}
          </p>
          {review.genres &&
            review.genres.map(genreId => {
              const g = genres?.find(x => x.id === genreId);
              const label = g ? g.title : `#${genreId}`;
              return (
                <p
                  key={g?.id}
                  className={cn(
                    'grow rounded-xl bg-[#0B0B0B] px-5 py-3 text-center md:grow-0',
                    label === 'string' && label.length >= 12 && 'col-span-2'
                  )}
                >
                  {label}
                </p>
              );
            })}
        </div>
      </section>
    </article>
  );
}
